import { Share2, Link as LinkIcon, Sparkles, Circle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner@2.0.3';

export interface NewsArticle {
  id: number;
  title: string;
  summary: string;
  image: string;
  sourceName: string;
  sourceLogo: string;
  relevancyScore: number;
  isRead: boolean;
  category: string;
  publishedDate: string;
  fullContent: string;
  url: string;
}

interface NewsMonitorPageProps {
  onArticleClick: (article: NewsArticle) => void;
  articles: NewsArticle[];
  onToggleRead: (articleId: number) => void;
}

export function NewsMonitorPage({ onArticleClick, articles, onToggleRead }: NewsMonitorPageProps) {
  const handleShare = (e: React.MouseEvent, article: NewsArticle) => {
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.summary,
        url: article.url,
      }).catch(() => {
        toast.success('Link copied to clipboard!');
      });
    } else {
      navigator.clipboard.writeText(article.url);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleCopyLink = (e: React.MouseEvent, article: NewsArticle) => {
    e.stopPropagation();
    navigator.clipboard.writeText(article.url);
    toast.success('Link copied to clipboard!');
  };

  const getRelevancyColor = (score: number) => {
    if (score >= 90) return 'from-green-500 to-emerald-600';
    if (score >= 75) return 'from-blue-500 to-cyan-600';
    if (score >= 60) return 'from-orange-500 to-amber-600';
    return 'from-gray-500 to-gray-600';
  };

  return (
    <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-4 sm:py-8">
      {/* Hero Banner */}
      <Card className="mb-4 sm:mb-6 overflow-hidden border-0 shadow-sm">
        <div 
          className="h-32 sm:h-48 bg-gradient-to-r from-orange-500 via-purple-600 to-blue-500 relative"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1504711434969-e33886168f5c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/80 via-purple-600/80 to-blue-500/80"></div>
          <div className="relative h-full flex flex-col justify-end p-4 sm:p-8">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full w-fit mb-2 sm:mb-3">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              <span className="text-white text-xs sm:text-sm">Discover</span>
            </div>
            <h1 className="text-white text-xl sm:text-4xl mb-1 sm:mb-2">NEWS MONITOR</h1>
            <p className="text-white/90 text-xs sm:text-sm">Stay updated with the latest industry trends and insights</p>
          </div>
        </div>
      </Card>

      {/* News Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {articles.map((article) => (
          <Card 
            key={article.id}
            className="border-0 shadow-sm hover:shadow-lg transition-all cursor-pointer group overflow-hidden"
            onClick={() => {
              onArticleClick(article);
              if (!article.isRead) {
                onToggleRead(article.id);
              }
            }}
          >
            <CardContent className="p-0">
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={article.image} 
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {/* Relevancy Score Badge */}
                <div className="absolute top-3 right-3">
                  <div className={`bg-gradient-to-r ${getRelevancyColor(article.relevancyScore)} text-white px-3 py-1 rounded-full shadow-lg flex items-center gap-1.5`}>
                    <Sparkles className="w-3 h-3" />
                    <span className="text-xs">{article.relevancyScore}% Match</span>
                  </div>
                </div>
                {/* Read/Unread Indicator */}
                <div className="absolute top-3 left-3">
                  <div className={`${article.isRead ? 'bg-gray-400' : 'bg-blue-500'} text-white px-2.5 py-1 rounded-full shadow-lg flex items-center gap-1`}>
                    <Circle className={`w-2 h-2 ${!article.isRead ? 'fill-white' : 'fill-gray-200'}`} />
                    <span className="text-[10px]">{article.isRead ? 'Read' : 'New'}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-5">
                {/* Source and Category */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                      <img 
                        src={article.sourceLogo} 
                        alt={article.sourceName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="text-xs text-gray-600">{article.sourceName}</span>
                  </div>
                  <Badge className="bg-gradient-to-r from-orange-500 to-purple-600 text-white border-0 text-[10px]">
                    {article.category}
                  </Badge>
                </div>

                {/* Title */}
                <h3 className="text-sm sm:text-base mb-2 group-hover:text-purple-600 transition-colors line-clamp-2 min-h-[2.5rem]">
                  {article.title}
                </h3>

                {/* Summary */}
                <p className="text-xs sm:text-sm text-gray-600 mb-4 line-clamp-3 min-h-[3.5rem]">
                  {article.summary}
                </p>

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-500">{article.publishedDate}</span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-gray-100"
                      onClick={(e) => handleShare(e, article)}
                      title="Share"
                    >
                      <Share2 className="w-3.5 h-3.5 text-gray-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-gray-100"
                      onClick={(e) => handleCopyLink(e, article)}
                      title="Copy Link"
                    >
                      <LinkIcon className="w-3.5 h-3.5 text-gray-600" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {articles.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-gray-900 mb-2">No relevant news yet</h3>
          <p className="text-sm text-gray-600">Check back later for the latest industry updates</p>
        </div>
      )}
    </div>
  );
}